import { Box, GlobalStyles, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import dico, { setAppLanguage } from "../../../util/src/language/dico";
import { CurrentRefereeInfo } from "../../../util/src/types";
import { useAppLanguage } from "../contexts/LanguageContext";
import Shape, { ShapeProps } from "../misc/Shape";

// Création des sons
const createBeep = (frequency: number, duration: number) => {
  const audioContext = new AudioContext();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.type = "sine";
  oscillator.frequency.value = frequency;

  gainNode.gain.value = 1;

  oscillator.start();
  setTimeout(() => {
    oscillator.stop();
    audioContext.close();
  }, duration);
};

// Buzzer sound
const createBuzzerSound = () => {
  const audioContext = new AudioContext();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  const lfoNode = audioContext.createOscillator();
  const lfoGain = audioContext.createGain();

  // Configuration de l'oscillateur principal
  oscillator.type = "square"; // Onde carrée pour un son plus "électronique"
  oscillator.frequency.value = 50; // Fréquence de base plus grave

  // Configuration du LFO (Low Frequency Oscillator) pour la modulation
  lfoNode.type = "square";
  lfoNode.frequency.value = 30; // Vitesse de la modulation
  lfoGain.gain.value = 50; // Intensité de la modulation

  // Connexion des nodes
  lfoNode.connect(lfoGain);
  lfoGain.connect(oscillator.frequency);

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  // Volume global
  gainNode.gain.value = 1;

  // Démarrage des oscillateurs
  oscillator.start();
  lfoNode.start();

  // Arrêt après la durée spécifiée
  setTimeout(() => {
    oscillator.stop();
    lfoNode.stop();
    audioContext.close();
  }, 600); // Durée totale du son
};

const referees = ["left", "chief", "right"] as const;

interface Timer {
  id: number;
  remaining: number;
}

interface AttemptTimersPayload {
  timers: { id: number; remaining: number; formatted?: string }[];
}

interface HomeProps {
  widget: boolean;
  serverUrl: string;
  display: boolean;
  theme: string;
}

export const Home: React.FC<HomeProps> = ({
  widget,
  serverUrl,
  display,
  theme,
}) => {
  // Liste des timers d'essai (un par essai validé) reçue depuis le serveur
  // Electron (apps/electron-app/src/handlers/web.ts). Source de vérité unique
  // pour tous les écrans/widgets, plus de setInterval local.
  // Subscribe so jury/timer chrome re-renders when Electron changes language
  useAppLanguage();
  const [timers, setTimers] = useState<Timer[]>([]);
  const [juryHasOverruled, setJuryHasOverruled] = useState(false);
  const [juryDiscussion, setJuryDiscussion] = useState(false);
  const juryColor = useRef(["white", "white"]);
  const [timer, setTimer] = useState("1:00");
  const [connectionError, setConnectionError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [shapeProps, setShapeProps] = useState<
    Record<(typeof referees)[number], ShapeProps[]>
  >(
    referees.reduce(
      (acc, ref) => {
        acc[ref] = [
          { variant: "circle", size: widget ? 11.25 : 50, zIndex: 2 },
          { variant: "circle", size: widget ? 50 : 280, zIndex: 2 },
          { variant: "rect", size: widget ? 12.25 : 60, zIndex: 3 },
        ];
        return acc;
      },
      {} as Record<(typeof referees)[number], ShapeProps[]>,
    ),
  );

  const formatTime = (totalSeconds: number) => {
    if (totalSeconds < 60) {
      return String(totalSeconds);
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const secondsStr = String(seconds).padStart(2, "0");
    return `${minutes}:${secondsStr}`;
  };

  useEffect(() => {
    const socket = io(
      serverUrl,
      display ? {} : { query: { page: window.location.pathname } },
    );

    const onConnectError = () => {
      setConnectionError(true);
    };

    const onConnect = () => {
      setConnectionError(false);
    };

    const onDisconnect = () => {
      const msg = dico().electronWeb.youHaveBeenDisconnected;
      window.location.replace(`/home?message=${encodeURIComponent(msg)}`);
    };

    const onTimerUpdate = (data: { timeLeft: number }) => {
      setTimer(formatTime(data.timeLeft));

      // Bip à 30 secondes
      if (data.timeLeft === 30) {
        createBeep(880, 200); // La4 (440Hz) pendant 200ms
      }
      // Bips pour les 3 dernières secondes
      if (data.timeLeft <= 3 && data.timeLeft > 0) {
        createBeep(880, 100); // La5 (880Hz) pendant 100ms
      }
      // Son type "Docteur Maboul" à 0 secondes
      if (data.timeLeft === 0) {
        createBuzzerSound();
      }
    };

    const onJuryStopShow = () => {
      setJuryDiscussion(true);
    };

    const onCurrentRefereeInfo = (data: CurrentRefereeInfo) => {
      setJuryDiscussion(false);
      setShapeProps((prev) => {
        // copie superficielle du record
        const next: typeof prev = { ...prev };

        const passed = referees.reduce(
          (acc, ref) => {
            acc[ref] = false;
            return acc;
          },
          {} as Record<(typeof referees)[number], boolean>,
        );

        // premier passage pour remplir next[…][0]
        data.result.forEach((d) => {
          if (
            d.referee_type == "left" ||
            d.referee_type == "right" ||
            d.referee_type == "chief"
          ) {
            const lights = [...next[d.referee_type]];
            if (lights[0]) {
              lights[0] = {
                ...lights[0],
                fill: d.passed === 1 ? "white" : "transparent",
              };
              if (d.passed !== 1 && lights[1] && lights[2]) {
                lights[1] = {
                  ...lights[1],
                  fill: "transparent",
                  color: "white",
                };
                lights[2] = {
                  ...lights[2],
                  fill: "transparent",
                  color: "white",
                };
              }
              next[d.referee_type] = lights;
              passed[d.referee_type] = d.passed === 1;
            }
          } else if (d.referee_type.includes("jury") && d.juryEnabled === 1) {
            setJuryHasOverruled(true);
            const color = d.color ?? "white";
            juryColor.current =
              color === "white"
                ? ["white", "transparent"]
                : color === "red"
                  ? ["red", "red"]
                  : ["red", color];
            setTimeout(() => {
              setJuryHasOverruled(false);
            }, 5000);
          }
        });

        if (Object.values(passed).some((p) => p === true)) {
          setIsVisible(true);
        }
        if (Object.values(passed).every((p) => p === true)) {
          // Le démarrage du timer d'essai est désormais géré côté serveur
          // (cf. apps/electron-app/src/handlers/web.ts). On colore juste les
          // lumières finales ; les timers arrivent via `attemptTimersUpdate`.
          data.result.forEach((d) => {
            if (
              d.referee_type == "left" ||
              d.referee_type == "right" ||
              d.referee_type == "chief"
            ) {
              const lights = [...next[d.referee_type]];
              if (lights[1] && lights[2]) {
                lights[1] = {
                  ...lights[1],
                  fill: d.color === "white" ? "white" : "red",
                  color: d.color === "white" ? "white" : "red",
                };
                lights[2] = {
                  ...lights[2],
                  fill: d.color === "white" ? "transparent" : d.color,
                  color: d.color === "white" ? "white" : d.color,
                };
                next[d.referee_type] = lights;
              }
            }
          });
        } else if (Object.values(passed).every((p) => p === false)) {
          setIsVisible(false);
        }

        /*else {
                referees.forEach(r => {
            const lights = [...next[r]];
          if(lights[1] && lights[2] && lights[0]) {
            lights[0] = {
                ...lights[0],
                fill: 'transparent'
            }
            lights[1] = {
              ...lights[1],
              fill: 'transparent',
              color:'white' 
            };
            lights[2] = {
              ...lights[2],
              fill: 'transparent',
              color: 'white'
            };
            next[r] = lights;
          }
        });
      }*/

        return next;
      });
    };

    const onAttemptTimersUpdate = (payload: AttemptTimersPayload) => {
      // Lights hide expired timers (remaining === 0); group grid keeps them
      const next = (payload?.timers ?? [])
        .filter((t) => t.remaining > 0)
        .map((t) => ({
          id: t.id,
          remaining: t.remaining,
        }));
      setTimers(next);
    };

    socket.off("connect_error", onConnectError);
    socket.off("connect", onConnect);
    socket.off("disconnected", onDisconnect);
    socket.off("timerUpdate", onTimerUpdate);
    socket.off("currentRefereeInfo", onCurrentRefereeInfo);
    socket.off("attemptTimersUpdate", onAttemptTimersUpdate);
    socket.off("juryStopShow", onJuryStopShow);
    const onLanguage = (nextLanguage: string) => {
      setAppLanguage(nextLanguage);
    };
    socket.on("connect_error", onConnectError);
    socket.on("connect", onConnect);
    socket.on("disconnected", onDisconnect);
    socket.on("timerUpdate", onTimerUpdate);
    socket.on("currentRefereeInfo", onCurrentRefereeInfo);
    socket.on("attemptTimersUpdate", onAttemptTimersUpdate);
    socket.on("juryStopShow", onJuryStopShow);
    socket.on("update-language", onLanguage);
    // Récupère l'état courant des timers d'essai à la connexion (utile si on
    // ouvre l'affichage en cours de round)
    socket.emit("requestAttemptTimers");
    socket.emit("get-language");

    return () => {
      socket.off("connect_error", onConnectError);
      socket.off("connect", onConnect);
      socket.off("disconnected", onDisconnect);
      socket.off("timerUpdate", onTimerUpdate);
      socket.off("currentRefereeInfo", onCurrentRefereeInfo);
      socket.off("attemptTimersUpdate", onAttemptTimersUpdate);
      socket.off("juryStopShow", onJuryStopShow);
      socket.off("update-language", onLanguage);
      socket.disconnect();
    };
  }, [display, serverUrl]);

  if (connectionError) {
    if (widget) return null;
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
          gap: 4,
        }}
      >
        <Typography
          sx={{
            color: "red",
            fontSize: "2rem",
            textAlign: "center",
            "@keyframes blink": {
              to: { visibility: "hidden" },
            },
            animation: "blink 2s steps(2, start) infinite",
          }}
        >
          {dico().electronWeb.unableToConnectToServer}
        </Typography>
        <Typography
          sx={{
            color: "white",
            fontSize: "1.5rem",
            textAlign: "center",
            maxWidth: "80%",
          }}
        >
          {dico().electronWeb.connectionErrorHelp}
        </Typography>
      </Box>
    );
  }

  if (widget) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
          transform:
            theme === "Challenge d'Hiver 2025"
              ? isVisible
                ? "translateX(0)"
                : "translateX(-100%)"
              : isVisible
                ? "translateY(0)"
                : "translateY(150%)",
          backgroundColor:
            theme === "Challenge d'Hiver 2025" ? "rgb(94,145,174)" : "black",
          borderRadius: "25px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {referees.map((r, index) => {
            return (
              <Box
                key={index}
                sx={{
                  m: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {shapeProps[r].map((shapeProp, idx) => (
                  <Shape {...shapeProp} key={idx} />
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <GlobalStyles
        styles={{
          "html, body": {
            margin: 0,
            padding: 0,
            overflow: "hidden",
            height: "100vh",
            maxHeight: "100vh",
          },
        }}
      />
      <Box sx={{ position: "absolute", padding: "2rem" }}>
        {/* Ticket time: ×5 vs historical 2rem / 1.5rem for arena readability */}
        <Typography
          sx={{ fontSize: "3rem", fontWeight: "bold", color: "white" }}
        >
          {dico().electronWeb.ticketTime}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {timers
            .sort((a, b) => b.id - a.id)
            .map((t) => (
              <Typography key={t.id} sx={{ color: "white", fontSize: "6rem" }}>
                {formatTime(t.remaining)}
              </Typography>
            ))}
        </Box>
      </Box>
      <Box
        sx={{
          height: "100svh",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {juryHasOverruled ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-evenly",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "4rem",
                textTransform: "uppercase",
                "@keyframes blink": {
                  to: { visibility: "hidden" },
                },
                animation: "blink 2s steps(2, start) infinite",
                maxWidth: "50%",
                textAlign: "center",
                color: "red",
                fontWeight: "bold",
              }}
            >
              {dico().electronWeb.juryOverruled}
            </Typography>
            <Box sx={{ position: "relative" }}>
              <Shape
                variant="circle"
                fill={juryColor.current[0]}
                color={juryColor.current[0]}
                size={200}
              />
              <Shape
                fill={juryColor.current[1]}
                sx={{
                  position: "absolute",
                  top: "60%",
                  left: "50%",
                  transform: "translate(-50%)",
                }}
                variant="rect"
                color={juryColor.current[1]}
                size={60}
              />
            </Box>
          </Box>
        ) : juryDiscussion ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "4rem",
                textTransform: "uppercase",
                "@keyframes blink": {
                  to: { visibility: "hidden" },
                },
                animation: "blink 2s steps(2, start) infinite",
                maxWidth: "80%",
                textAlign: "center",
                color: "white",
                fontWeight: "bold",
              }}
            >
              {dico().electronWeb.juryDiscuss}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-evenly",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: "5rem",
            }}
          >
            {referees.map((r, index) => {
              return (
                <Box
                  key={index}
                  sx={{
                    m: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  {shapeProps[r].map((shapeProp, idx) => (
                    <Shape key={idx} {...shapeProp} />
                  ))}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      {!juryHasOverruled && !juryDiscussion && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translate(-50%)",
          }}
        >
          <Typography
            sx={[
              { fontSize: "10rem", fontWeight: "bold", color: "white" },
              timer === "0"
                ? {
                    "@keyframes blink": {
                      to: { visibility: "hidden" },
                    },
                    animation: "blink 2s steps(2, start) infinite",
                    color: "red",
                  }
                : {},
            ]}
          >
            {timer}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
